import { deriveGraph, type EdgeCategory } from '@agavi/blocks';
import { Trophy } from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { computeLayout, type Placed } from './builder-layout';

import type { Task } from '@/types/experience';
import type { MutableRefObject } from 'react';

/**
 * Abstract overview of the activity, from the SAME `deriveGraph` + `computeLayout` the board uses, so
 * positions mirror the board exactly. Tasks are anonymous blocks (no titles/numbers — you scan by
 * POSITION, not by reading). Connector language:
 *   - the correct through-line reads at full weight (blue) with a tiny arrowhead for direction (a correct
 *     path can run BACK up the chain inside a side pathway, so direction has to be legible);
 *   - every other connection is a grey hairline;
 *   - a block's connections (in AND out) light to their category colour when you hover/focus that block.
 * Connectors run edge-to-edge like the board — the arrowhead TIP lands exactly on the target block's edge
 * and the line meets the arrow's BASE along the entry normal, so nothing floats in a gap or enters at a kink.
 * Blocks auto-scale so a max of four columns fit the panel width (wider structures scroll).
 *   - `mode='navigate'` → the builder overview: click a block to jump the board there; hovering it rings
 *     the matching card in the workspace (`onHoverTask`); an optional viewport box shows/scrolls the view.
 *   - `mode='pick'`     → the flow target picker: focusing a block also reveals its title.
 * In both modes the ACT is deliberate: hover-then-click (mouse), tap-again (touch), or Tab-then-Enter.
 */

// Base dimensions at scale 1; the real DIMS scale so four columns span the available width.
const BASE = { cw: 52, ch: 32, vg: 9, cgap: 22 };
const FOUR_COL = 4 * BASE.cw + 3 * BASE.cgap; // width of four columns at scale 1 (pre-layout-padding)
const AL = 5; // arrowhead length (base → tip)
const AW = 2.8; // arrowhead half-width

const CAT_COLOR: Record<EdgeCategory, string> = {
  route: 'var(--color-ws-primary)',
  // Muted, not error red. Default is "nothing above matched", not a verdict on the student.
  fallback: 'var(--color-ws-muted)',
  gate: 'var(--color-ws-muted)',
  sensor: 'var(--color-ws-warning-fg)',
};
const SPECIAL_COLOR = 'var(--color-ws-text-link)';
const edgeColor = (cat: EdgeCategory, special?: boolean) => (special ? SPECIAL_COLOR : CAT_COLOR[cat]);

export interface ViewportFrac { topFrac: number; heightFrac: number; leftFrac: number; widthFrac: number }

export interface MiniMapProps {
  tasks: Task[];
  mode?: 'navigate' | 'pick';
  /** The task you're viewing/editing — ringed as "you are here". */
  currentTaskId?: string | null;
  /** Pick mode: the currently-chosen goto target — filled. */
  selectedId?: string | null;
  /** Pick mode: nodes that can't be chosen (at least the task itself). */
  excludeIds?: Set<string>;
  /** Pick mode: the category of the event being wired, so the flow line matches Flow's colour + terminator
   * (→ correct, ✕ incorrect, ○ incomplete/neutral, ★ special) instead of always reading as correct. */
  /** The node for `currentTaskId`, so a dialog can open with focus on where the student IS rather
   * than on whatever happens to be first — focusing any OTHER node draws a flow line to it, which
   * reads as a destination someone chose. An object ref, because this component reads it too. */
  currentNodeRef?: MutableRefObject<HTMLButtonElement | null>;
  /** Scroll the current task to the middle whenever the layout changes. See the effect below. */
  revealCurrent?: boolean;
  pickCategory?: EdgeCategory;
  pickSpecial?: boolean;
  onPick?: (taskId: string) => void;
  onJump?: (taskId: string) => void;
  /** Navigate: the hovered/focused block, so the board can ring the matching card (null = none). */
  onHoverTask?: (taskId: string | null) => void;
  /** Navigate: the board's currently-visible region as fractions of scrollable content. */
  viewport?: ViewportFrac | null;
  /** Navigate: dragging the viewport box scrolls the board to these fractions. */
  onScrollToFrac?: (topFrac: number, leftFrac: number) => void;
}

export function MiniMap({ tasks, mode = 'navigate', currentTaskId, selectedId, excludeIds, currentNodeRef, revealCurrent, pickCategory = 'route', pickSpecial, onPick, onJump, onHoverTask, viewport, onScrollToFrac }: MiniMapProps) {
  const { t } = useTranslation();

  // Measure the visible area: width sets the scale so four columns span it; a SHORT viewport (landscape
  // phone) caps the scale lower so blocks shrink and more of the structure fits the limited height.
  const wrapRef = useRef<HTMLDivElement>(null);
  const [avail, setAvail] = useState({ w: 260, h: 420 });
  useEffect(() => {
    const wrap = wrapRef.current;
    const parent = wrap?.parentElement;
    if (!wrap || !parent) return;
    const measure = () => setAvail({ w: wrap.clientWidth, h: parent.clientHeight });
    const ro = new ResizeObserver(measure);
    ro.observe(parent);
    measure();
    return () => ro.disconnect();
  }, []);
  // Keep blocks at their base size (never shrink for a short/landscape-phone viewport) so they stay
  // tappable — a taller structure just scrolls. Only a NARROW width scales them down (four-column fit).
  const k = Math.min(1, Math.max(0.6, (avail.w - 20) / FOUR_COL)); // -20 = computeLayout's width padding
  const DIMS = useMemo(() => ({ cw: Math.round(BASE.cw * k), ch: Math.round(BASE.ch * k), vg: Math.round(BASE.vg * k), cgap: Math.round(BASE.cgap * k) }), [k]);

  const graph = useMemo(() => deriveGraph(tasks), [tasks]);
  const byId = useMemo(() => new Map(tasks.map((tk) => [tk.id, tk])), [tasks]);
  const layout = useMemo(() => computeLayout(graph.spine, graph.detoursOf, DIMS), [graph, DIMS]);

  /**
   * Put the current task in the middle, and do it again every time the layout moves.
   *
   * `avail` starts at a placeholder 260px and is replaced by a real measurement one paint later, so
   * the whole board is laid out small and then rescales — on a wide dialog by about 1.67x. Revealing
   * the node once, on mount, therefore scrolled to where it was BEFORE the rescale, which is roughly
   * 60% of the way to where it ended up: the reported "only scrolls about halfway down". Keying off
   * `layout` catches the rescale, and anything else that moves the board, for free.
   */
  useEffect(() => {
    if (!revealCurrent) return;
    currentNodeRef?.current?.scrollIntoView({ block: 'center', inline: 'center' });
  }, [revealCurrent, currentNodeRef, layout]);
  const spineSet = useMemo(() => new Set(graph.spine), [graph.spine]);
  // Tasks that END the experience: no outgoing correct through-line (correct → End, or a dead-end),
  // so finishing them correctly drops the student out. Mirrors the board's End-cap set.
  const continues = useMemo(() => {
    const s = new Set<string>();
    for (const e of graph.edges) if (e.toId && e.cat === 'route') s.add(e.fromId);
    return s;
  }, [graph.edges]);

  // Focus (mouse hover / first touch tap / keyboard Tab) — drives the deliberate tap-again ACT, lights the
  // block's connections, and (navigate) rings the workspace card. Never pre-set from a touch tap.
  const [focusId, setFocusId] = useState<string | null>(null);
  const hoverCb = useRef(onHoverTask); hoverCb.current = onHoverTask;
  useEffect(() => { if (mode === 'navigate') hoverCb.current?.(focusId); }, [focusId, mode]);
  useEffect(() => () => hoverCb.current?.(null), []); // clear the board ring when the map closes

  const p = (id: string) => layout.placed.get(id);
  const isExcluded = (id: string) => mode === 'pick' && !!excludeIds?.has(id);
  const edgeIncident = (fromId: string, toId: string | null) => !!focusId && (fromId === focusId || toId === focusId);

  function act(id: string) { if (isExcluded(id)) return; if (mode === 'pick') onPick?.(id); else onJump?.(id); }
  function onNodeClick(id: string) { if (focusId === id) act(id); else setFocusId(id); } // tap-again to commit

  const cx = (pl: Placed) => pl.x + DIMS.cw / 2;
  const cy = (pl: Placed) => pl.y + DIMS.ch / 2;

  // Board-faithful routing. The arrow TIP lands on the target edge; the line ends at the arrow BASE
  // (tip pulled back by AL along the entry normal) and approaches along that normal, so there's no gap
  // and no kink. Through-line (`loose`) enters the top going down; a redirect enters a side edge going
  // in; a same-column redirect bows into the gap and enters the right edge. Nothing cuts across a block.
  function connector(a: Placed, b: Placed, through: boolean): { d: string; tip: { x: number; y: number }; angle: number } {
    const cw = DIMS.cw;
    if (through) {
      const tip = { x: cx(b), y: b.y };
      return { d: `M${cx(a)} ${a.y + DIMS.ch} L${cx(b)} ${b.y - AL}`, tip, angle: 90 }; // down into top edge
    }
    const sameCol = Math.abs(a.x - b.x) < 1;
    if (sameCol) {
      const rx = a.x + cw, bow = Math.min(30, 14 + Math.abs(b.y - a.y) * 0.1);
      const tip = { x: b.x + cw, y: cy(b) };
      return { d: `M${rx} ${cy(a)} C${rx + bow} ${cy(a)}, ${rx + bow} ${cy(b)}, ${tip.x + AL} ${cy(b)}`, tip, angle: 180 }; // left into right edge
    }
    if (b.x >= a.x) {
      const tip = { x: b.x, y: cy(b) };
      const sx = a.x + cw, bx = b.x - AL, mx = (sx + bx) / 2;
      return { d: `M${sx} ${cy(a)} C${mx} ${cy(a)}, ${mx} ${cy(b)}, ${bx} ${cy(b)}`, tip, angle: 0 }; // right into left edge
    }
    const tip = { x: b.x + cw, y: cy(b) };
    const sx = a.x, bx = tip.x + AL, mx = (sx + bx) / 2;
    return { d: `M${sx} ${cy(a)} C${mx} ${cy(a)}, ${mx} ${cy(b)}, ${bx} ${cy(b)}`, tip, angle: 180 }; // left into right edge (return)
  }

  const focusTask = mode === 'pick' && focusId ? byId.get(focusId) : null;
  const focusPos = mode === 'pick' && focusId ? p(focusId) : null;
  // Float the title to the SIDE of the block (not above/below) so it never covers neighbouring blocks,
  // and on the side OPPOSITE the connector's entry so it doesn't hide the connecting symbol. The flow
  // line enters the block's RIGHT edge for a same-column / leftward (return) target and its LEFT edge for
  // a rightward (forward) target → label goes left / right respectively. Falls back to position.
  const labelCur = currentTaskId ? p(currentTaskId) : null;
  let labelLeft = !!focusPos && focusPos.x + DIMS.cw > layout.width * 0.55;
  if (focusPos && labelCur && focusId !== currentTaskId) {
    labelLeft = Math.abs(labelCur.x - focusPos.x) < 1 || focusPos.x < labelCur.x;
  }

  // Pick mode: everything is ghosted except the CURRENT task (the source) and the connection TARGET. The
  // target is the candidate you're focusing (a live preview of the connection) or, absent a focus, the
  // event's already-chosen goto (`selectedId`) — either way it gets a ring and a visible flow line from
  // the current task. `pick` gates all of this so navigate/overview is untouched.
  const pick = mode === 'pick';
  const validFocus = pick && focusId && !isExcluded(focusId) ? focusId : null;
  const activeTarget = pick ? (validFocus ?? selectedId ?? null) : null;

  return (
    <div ref={wrapRef} className='w-full'>
      {tasks.length === 0 ? (
        <p className='py-8 text-center text-sm text-ws-muted'>{t('experience.map.empty')}</p>
      ) : (
        <div className='relative mx-auto' style={{ width: layout.width, height: layout.height }}>
          {/* Connectors. Decorative to AT (the node buttons carry the meaning). In PICK mode the whole
              graph is faded to context and one prominent flow line current→target is drawn on top. */}
          <svg aria-hidden='true' className='pointer-events-none absolute inset-0 overflow-visible' width={layout.width} height={layout.height}>
            {graph.edges.map((e, i) => {
              const a = e.toId ? p(e.fromId) : null;
              const b = e.toId ? p(e.toId) : null;
              if (!a || !b) return null;
              const through = e.kind === 'loose';
              const { d, tip, angle } = connector(a, b, through);
              if (pick) return <path key={i} d={d} stroke='var(--color-ws-muted)' strokeWidth={0.75} fill='none' opacity={0.12} />;
              const correct = !e.special && e.cat === 'route';
              const incident = edgeIncident(e.fromId, e.toId);
              const color = incident ? edgeColor(e.cat, e.special) : correct ? 'var(--color-ws-primary)' : 'var(--color-ws-muted)';
              const width = incident ? 2 : correct ? 1.5 : 0.9;
              return (
                <g key={i} opacity={incident ? 1 : correct ? 0.95 : 0.6}>
                  <path d={d} stroke={color} strokeWidth={width} fill='none' />
                  <Arrow tip={tip} angle={angle} color={color} />
                </g>
              );
            })}
            {graph.looseThreads.map((lt, i) => {
              const a = p(lt.fromId); if (!a) return null;
              const incident = edgeIncident(lt.fromId, null);
              return <path key={`lt-${i}`} d={`M${a.x + DIMS.cw} ${cy(a)} l12 0`} stroke={pick ? 'var(--color-ws-muted)' : incident ? 'var(--color-ws-warning-fg)' : 'var(--color-ws-muted)'} strokeWidth={incident && !pick ? 2 : 0.9} fill='none' opacity={pick ? 0.12 : incident ? 1 : 0.6} />;
            })}
            {/* Pick mode: the connection being made/inspected — current task → target, drawn prominently in
                the EVENT's colour + terminator (matches Flow), so wiring an incomplete/incorrect/special
                doesn't read as a correct link. */}
            {pick && activeTarget && currentTaskId && activeTarget !== currentTaskId && (() => {
              const a = p(currentTaskId), b = p(activeTarget);
              if (!a || !b) return null;
              const { d, tip, angle } = connector(a, b, false);
              const c = edgeColor(pickCategory, pickSpecial);
              const dashed = !pickSpecial && (pickCategory === 'gate' || pickCategory === 'sensor');
              return <g><path d={d} stroke={c} strokeWidth={2.25} fill='none' strokeDasharray={dashed ? '4 3' : undefined} /><FlowCap category={pickCategory} special={pickSpecial} tip={tip} angle={angle} color={c} /></g>;
            })()}
          </svg>

          {/* Viewport box (navigate) — the region currently visible in the workspace. Below the node
              buttons so blocks stay clickable; drag it from any gap to scroll the board. */}
          {mode === 'navigate' && viewport && onScrollToFrac && (
            <ViewportBox frac={viewport} width={layout.width} height={layout.height} onScrollToFrac={onScrollToFrac} label={t('experience.map.viewport')} />
          )}

          {[...layout.placed.values()].map((pl) => {
            const task = byId.get(pl.id);
            if (!task) return null;
            const isCurrent = pl.id === currentTaskId;
            const isSelected = pick && pl.id === selectedId;
            const isFocus = pl.id === focusId;
            const isTarget = pick && pl.id === activeTarget && !isCurrent;
            const isSpine = spineSet.has(pl.id);
            const excluded = isExcluded(pl.id);
            const ends = !continues.has(pl.id);
            const label = task.title || t('nav.newExperience');
            const points = task.points ?? 0;
            // Pick mode ghosts everything except the current task, the connection target, and whatever
            // you're focusing (so a ghosted candidate lights up as you go to pick it). Navigate = no ghost.
            const pickGhost = pick && !isCurrent && !isTarget && !isFocus;
            const state = isCurrent
              ? 'border-ws-primary bg-ws-primary-subtle ring-1 ring-ws-primary'
              : isTarget
                ? 'border-ws-primary bg-ws-primary-subtle ring-1 ring-ws-primary'
                : isFocus
                  ? 'border-ws-primary bg-ws-primary-subtle shadow-md'
                  : 'border-ws-border bg-ws-primary-subtle hover:border-ws-primary';
            const tabW = Math.round(DIMS.cw * 0.42);
            return (
              <Fragment key={pl.id}>
                <button
                  type='button'
                  // A callback rather than the object ref directly: React 18's `ref` prop will not
                  // take a `RefObject<T | null>`, and the ref has to be nullable for this component
                  // to read it.
                  ref={(el) => { if (pl.id === currentTaskId && currentNodeRef) currentNodeRef.current = el; }}
                  onClick={() => onNodeClick(pl.id)}
                  onPointerEnter={(e) => { if (e.pointerType === 'mouse') setFocusId(pl.id); }}
                  onPointerLeave={(e) => { if (e.pointerType === 'mouse') setFocusId((f) => (f === pl.id ? null : f)); }}
                  onFocus={() => setFocusId(pl.id)}
                  onBlur={() => setFocusId((f) => (f === pl.id ? null : f))}
                  aria-label={(mode === 'pick' ? t('experience.map.pickTarget', { title: label }) : t('experience.map.jumpTo', { title: label })) + (isSpine ? '' : ` (${t('experience.builder.branches')})`) + (ends ? ` — ${t('experience.map.endsHere')}` : '')}
                  aria-current={isCurrent ? 'true' : undefined}
                  aria-pressed={mode === 'pick' ? isSelected : undefined}
                  aria-disabled={excluded || undefined}
                  className={`absolute z-10 flex items-center justify-center overflow-hidden rounded-md border transition-[opacity,box-shadow,border-color] focus:outline-none focus-visible:ring-2 focus-visible:ring-ws-primary ${pickGhost ? 'opacity-[0.45]' : 'opacity-100'} ${excluded ? 'cursor-default' : ''} ${state}`}
                  style={{ left: pl.x, top: pl.y, width: DIMS.cw, height: DIMS.ch }}
                >
                  {points > 0 && (
                    <span className='flex items-center gap-0.5 text-[9px] font-semibold tabular-nums text-ws-text-link'>
                      <Trophy className='h-2.5 w-2.5' aria-hidden='true' />{points}
                    </span>
                  )}
                </button>
                {/* Ends the experience — a grey cap protruding from the bottom edge, mirroring the board's
                    End tab (a corner glyph reads as a dot at this scale). The through-line has already
                    left, so the bottom-centre is free. */}
                {ends && <span aria-hidden='true' title={t('experience.map.endsHere')} className={`absolute rounded-b-[3px] bg-ws-muted ${pickGhost ? 'opacity-30' : ''}`} style={{ left: pl.x + (DIMS.cw - tabW) / 2, top: pl.y + DIMS.ch - 1, width: tabW, height: 4 }} />}
              </Fragment>
            );
          })}

          {/* Pick mode only — the focused block's title, to the SIDE so it doesn't cover other blocks. */}
          {focusTask && focusPos && (
            <div
              className='pointer-events-none absolute z-20 max-w-[140px] truncate rounded-md bg-ws-text px-2 py-1 text-[11px] font-medium text-ws-surface shadow-lg'
              style={labelLeft
                ? { right: layout.width - focusPos.x + 6, top: focusPos.y + DIMS.ch / 2 - 11 }
                : { left: focusPos.x + DIMS.cw + 6, top: focusPos.y + DIMS.ch / 2 - 11 }}
            >
              {focusTask.title || t('nav.newExperience')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Draggable outline of the board's currently-visible region. Interior is pointer-transparent so the
 * blocks under it stay clickable; you grab the box from any gap and drag to scroll the board. */
function ViewportBox({ frac, width, height, onScrollToFrac, label }: { frac: ViewportFrac; width: number; height: number; onScrollToFrac: (t: number, l: number) => void; label: string }) {
  const drag = useRef<{ px: number; py: number; top: number; left: number } | null>(null);
  const PAD = 8; // outset so the box border sits in the gaps, not directly under block borders
  const bw = Math.max(12, frac.widthFrac * width) + PAD * 2;
  const bh = Math.max(12, frac.heightFrac * height) + PAD * 2;
  function onDown(e: React.PointerEvent) {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { px: e.clientX, py: e.clientY, top: frac.topFrac, left: frac.leftFrac };
  }
  function onMove(e: React.PointerEvent) {
    if (!drag.current) return;
    const dTop = (e.clientY - drag.current.py) / height;
    const dLeft = (e.clientX - drag.current.px) / width;
    const clamp = (v: number, size: number) => Math.min(Math.max(v, 0), Math.max(0, 1 - size));
    onScrollToFrac(clamp(drag.current.top + dTop, frac.heightFrac), clamp(drag.current.left + dLeft, frac.widthFrac));
  }
  function onUp(e: React.PointerEvent) { drag.current = null; (e.target as HTMLElement).releasePointerCapture(e.pointerId); }
  return (
    <div
      // Decorative pointer-only affordance (keyboard users scroll by Tab-to-block + Enter, which jumps
      // there). No z-index → above the connectors (later in DOM) but BELOW the node buttons (z-10), so a
      // tap always reaches a block; you drag the box from the gaps between blocks.
      aria-hidden='true'
      title={label}
      className='absolute rounded border-2 border-ws-primary/35 bg-ws-primary/[0.04] cursor-grab active:cursor-grabbing'
      style={{ left: frac.leftFrac * width - PAD, top: frac.topFrac * height - PAD, width: bw, height: bh }}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
    />
  );
}

/** A tiny direction arrowhead whose TIP sits at `tip` (on the block edge), pointing along `angle`
 * (0 = right / into a left edge, 90 = down / into a top edge, 180 = left / into a right edge). */
function Arrow({ tip, angle, color }: { tip: { x: number; y: number }; angle: number; color: string }) {
  return <path d={`M0 0 L${-AL} ${-AW} L${-AL} ${AW} Z`} fill={color} transform={`translate(${tip.x} ${tip.y}) rotate(${angle})`} />;
}

/**
 * The terminator says which FAMILY of rung produced the wire, by SHAPE first and colour second —
 * a colour alone is not a distinction a reader can rely on, and two of these are grey.
 *
 *   ➤  route     filled arrow, the through-line
 *   ▻  fallback  the same arrow, hollow: still a route, just the one nothing claimed. It replaced
 *                an error-red ✕, which said the student got something wrong about a rung that
 *                only means "nothing above matched".
 *   ○  gate      hollow circle — no usable answer yet
 *   ⊘  sensor    the same circle with a bar, so a hardware failure is not merely a differently
 *                coloured version of a blank
 *   ★  special   an authored event
 */
function FlowCap({ category, special, tip, angle, color }: { category: EdgeCategory; special?: boolean; tip: { x: number; y: number }; angle: number; color: string }) {
  if (special) return <text x={tip.x} y={tip.y} dy='0.32em' textAnchor='middle' fontSize='11' fill={color}>★</text>;
  if (category === 'route') return <Arrow tip={tip} angle={angle} color={color} />;
  if (category === 'fallback') {
    return <path d={`M0 0 L${-AL} ${-AW} L${-AL} ${AW} Z`} fill='none' stroke={color} strokeWidth={1.25}
      transform={`translate(${tip.x} ${tip.y}) rotate(${angle})`} />;
  }
  if (category === 'sensor') {
    return (
      <g stroke={color} strokeWidth={1.5} fill='none'>
        <circle cx={tip.x} cy={tip.y} r={3.5} />
        <line x1={tip.x - 2.5} y1={tip.y + 2.5} x2={tip.x + 2.5} y2={tip.y - 2.5} />
      </g>
    );
  }
  return <circle cx={tip.x} cy={tip.y} r={3.5} fill='none' stroke={color} strokeWidth={1.5} />; // gate
}
